/* Reuse the theme's Flickity engine and deferred videos; never clone carousel cards. */
(() => {
  if (customElements.get('product-customer-videos')) return;
  let activeRow = null;

  customElements.define('product-customer-videos', class extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      // Capture before video-media's inline playback handler receives the click.
      this.addEventListener('click', event => {
        const trigger = event.target.closest('.deferred-poster');
        if (!trigger || !this.contains(trigger)) return;
        const player = trigger.closest('video-media');
        const source = player?.querySelector('template')?.content.querySelector('video');
        if (!source) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.carousel?.isPreventingClicks) return;
        this.openVideo(source, player, trigger);
      }, { ...options, capture: true });
      this.querySelectorAll('.deferred-poster').forEach(button => button.setAttribute('aria-haspopup', 'dialog'));
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.pauseAll();
          this.modalVideo?.pause();
        }
      }, options);
      this.closest('.shopify-section')?.addEventListener('shopify:section:unload', () => this.closeVideo(), options);
      if (window.Flickity) this.initCarousel();
      else customElements.whenDefined('carousel-element').then(() => {
        if (this.isConnected) this.initCarousel();
      });
    }

    initCarousel() {
      if (this.carousel) return;
      this.track = this.querySelector('.product-customer-videos__track');
      const cards = this.track?.querySelectorAll('.product-customer-videos__card');
      if (!cards?.length) return;
      this.carousel = new Flickity(this.track, {
        cellSelector: '.product-customer-videos__card', cellAlign: getComputedStyle(this).direction === 'rtl' ? 'right' : 'left',
        rightToLeft: getComputedStyle(this).direction === 'rtl',
        contain: true, wrapAround: true, groupCells: false,
        prevNextButtons: false, pageDots: false, accessibility: false,
        draggable: cards.length > 1, percentPosition: false
      });
      const options = { signal: this.abortController.signal };
      // The bundled Flickity binds only touch events on hybrid devices.
      if ('ontouchstart' in window) {
        this.carousel.viewport.addEventListener('mousedown', event => {
          if (!this.carousel.isDraggable || this.carousel.isPointerDown || event.button !== 0) return;
          this.mouseDragging = true;
          this.carousel.onmousedown(event);
        }, options);
        window.addEventListener('mousemove', event => {
          if (this.mouseDragging) this.carousel.onmousemove(event);
        }, options);
        window.addEventListener('mouseup', event => {
          if (!this.mouseDragging) return;
          this.carousel.onmouseup(event);
          this.mouseDragging = false;
        }, options);
      }
      const navigate = direction => {
        if (!this.carousel.options.wrapAround) return;
        this.closeVideo();
        this.carousel[direction](true, matchMedia('(prefers-reduced-motion: reduce)').matches);
      };
      this.querySelector('[data-video-previous]')?.addEventListener('click', () => navigate('previous'), options);
      this.querySelector('[data-video-next]')?.addEventListener('click', () => navigate('next'), options);
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const forward = (event.key === 'ArrowRight') !== this.carousel.options.rightToLeft;
        navigate(forward ? 'next' : 'previous');
      }, options);
      this.track.addEventListener('wheel', event => {
        if (!this.carousel.options.wrapAround || event.ctrlKey) return;
        const delta = event.deltaX || (event.shiftKey ? event.deltaY : 0);
        if (!delta || (!event.shiftKey && Math.abs(event.deltaY) >= Math.abs(delta))) return;
        // Leave vertical page scrolling and trackpad pinch zoom untouched.
        event.preventDefault();
        const scale = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.track.clientWidth : 1;
        if (event.timeStamp - (this.lastWheelTime || 0) > 200 || Math.sign(delta) !== Math.sign(this.wheelDelta || delta)) this.wheelDelta = 0;
        this.lastWheelTime = event.timeStamp;
        this.wheelDelta = (this.wheelDelta || 0) + delta * scale;
        const step = this.carousel.cells[0].size.outerWidth / 2;
        if (Math.abs(this.wheelDelta) < step) return;
        const forward = (this.wheelDelta > 0) !== this.carousel.options.rightToLeft;
        this.wheelDelta %= step;
        navigate(forward ? 'next' : 'previous');
      }, { ...options, passive: false });
      this.carousel.on('dragStart', () => { this.pauseAll(); this.closeVideo(); });
      this.carousel.on('change', () => { this.pauseAll(); this.closeVideo(); });
      // Flickity suppresses the click generated after a swipe.
      this.addEventListener('shopify:block:select', event => {
        const card = event.target.closest('.product-customer-videos__card');
        const index = this.carousel.cells.findIndex(cell => cell.element === card);
        if (index >= 0) this.carousel.select(index, true, true);
      }, options);
      this.resizeObserver = new ResizeObserver(() => {
        const total = this.carousel.cells.reduce((sum, cell) => sum + cell.element.getBoundingClientRect().width + (parseFloat(getComputedStyle(cell.element).marginInlineEnd) || 0), 0);
        const loop = cards.length > 1 && total > this.track.clientWidth + 1;
        this.carousel.options.wrapAround = loop;
        this.carousel.options.draggable = loop;
        this.carousel.updateDraggable();
        this.carousel.resize();
        const navigation = this.querySelector('.product-customer-videos__navigation');
        if (navigation) navigation.hidden = !loop;
      });
      this.resizeObserver.observe(this.track);
    }

    pauseAll() {
      this.querySelectorAll('video').forEach(video => video.pause());
      this.querySelectorAll('video-media[playing]').forEach(player => player.pause());
    }

    openVideo(source, player, trigger) {
      activeRow?.closeVideo();
      document.querySelectorAll('product-customer-videos').forEach(row => row.pauseAll());
      const template = this.querySelector('[data-customer-video-dialog]');
      if (!template) return;
      this.dialog = template.content.firstElementChild.cloneNode(true);
      this.modalVideo = source.cloneNode(true);
      const video = this.modalVideo;
      video.controls = true;
      video.playsInline = true;
      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.removeAttribute('muted');
      video.muted = false;
      video.defaultMuted = false;
      video.volume = 1;
      video.loop = false;
      video.preload = 'metadata';
      video.className = 'customer-video-modal__video';
      const ratio = player.style.getPropertyValue('--aspect-ratio').trim();
      this.dialog.style.setProperty('--customer-video-ratio', ratio || '9 / 16');
      video.addEventListener('loadedmetadata', () => {
        if (this.modalVideo === video && video.videoWidth && video.videoHeight) {
          this.dialog?.style.setProperty('--customer-video-ratio', video.videoWidth / video.videoHeight);
        }
      }, { once: true });
      this.dialog.append(video);
      this.dialog.querySelector('button').addEventListener('click', () => this.closeVideo());
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.closeVideo();
      });
      this.dialog.addEventListener('click', event => {
        if (event.target === this.dialog) this.closeVideo();
      });
      this.dialog.addEventListener('close', () => {
        if (this.dialog) this.closeVideo();
      });
      this.trigger = trigger;
      this.lockScroll();
      document.body.append(this.dialog);
      this.dialog.showModal();
      activeRow = this;
      // Stay in the user activation call stack for audible iOS/Android playback.
      video.play()?.catch(() => { /* Native controls remain available if playback is blocked. */ });
    }

    lockScroll() {
      this.scrollPosition = { x: window.scrollX, y: window.scrollY };
      this.bodyStyle = document.body.getAttribute('style');
      const scrollbar = window.innerWidth - document.documentElement.clientWidth;
      const padding = parseFloat(getComputedStyle(document.body).paddingRight) || 0;
      Object.assign(document.body.style, {
        position: 'fixed', top: `${-this.scrollPosition.y}px`,
        left: `${-this.scrollPosition.x}px`, width: '100%', overflow: 'hidden',
        paddingRight: `${padding + scrollbar}px`
      });
    }

    closeVideo() {
      if (!this.dialog) return;
      this.modalVideo?.pause();
      const dialog = this.dialog;
      this.dialog = null;
      this.modalVideo?.removeAttribute('src');
      this.modalVideo?.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
      this.modalVideo?.load();
      dialog.close();
      dialog.remove();
      this.modalVideo = null;
      if (this.bodyStyle === null) document.body.removeAttribute('style');
      else document.body.setAttribute('style', this.bodyStyle);
      window.scrollTo({ left: this.scrollPosition.x, top: this.scrollPosition.y, behavior: 'instant' });
      if (this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
      this.trigger = null;
      if (activeRow === this) activeRow = null;
    }

    disconnectedCallback() {
      this.closeVideo();
      this.pauseAll();
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.carousel?.destroy();
      this.carousel = null;
    }
  });
})();
